import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormDialog } from '@/components/shared/form-dialog';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState } from '@/components/shared/states';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MemberPhoto } from '@/components/ui/member-photo';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { PhoneInput } from '@/components/ui/phone-input';
import { Spinner } from '@/components/ui/spinner';
import { SwitchField } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { queryKeys } from '@/lib/query-client';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatDate, formatDateTime } from '@/utils/format';

import { BasicMemberFields, MoreMemberFields } from './member-form-fields';
import { MemberFilesCard } from './member-files-card';
import { MemberGivingTab } from './member-giving-tab';
import { MemberInvolvementCard } from './member-involvement-card';
import { MemberRecordGivingDialog } from './member-record-giving-dialog';
import {
  emptyMemberForm,
  FAMILY_RELATIONSHIPS,
  formToPayload,
  memberToForm,
  type FamilyRelationship,
  type MemberFormState,
  type MemberRecord,
} from './member-form-state';
import {
  formatMemberHistoryValue,
  memberHistoryFieldLabel,
} from './member-history-format';
import { formatFamilyRelationship, formatFamilyRelationshipPreview } from './member-relationship-label';
import { MemberDetailNavigation } from './member-detail-navigation';
import { memberDetailPath, membersListPath, membersListToApiQuery, parseMembersListState } from './members-list-state';

interface ChangeRow {
  id: string;
  field: string;
  beforeValue: string | null;
  afterValue: string | null;
  actorName: string | null;
  createdAt: string;
}

interface FamilyLink {
  id: string;
  relationship: FamilyRelationship;
  notes: string | null;
  inferred?: boolean;
  isExternal?: boolean;
  relatedExternalName?: string | null;
  relatedExternalPhone?: string | null;
  related: {
    id: string;
    firstName: string;
    middleName: string | null;
    lastName: string;
    gender: 'female' | 'male' | 'other' | 'prefer_not_to_say' | null;
    status: string;
    photoUrl: string | null;
  } | null;
}

interface FamilyTree {
  parents: FamilyLink[];
  unclesAunts: FamilyLink[];
  spouses: FamilyLink[];
  siblings: FamilyLink[];
  children: FamilyLink[];
  niecesNephews: FamilyLink[];
  cousins: FamilyLink[];
  grandparents: FamilyLink[];
  grandchildren: FamilyLink[];
  other: FamilyLink[];
  peerLinks?: Array<{
    fromMemberId: string;
    toMemberId: string;
    relationship: 'sibling';
    inferred: true;
  }>;
}

const displayName = (person: {
  firstName: string;
  middleName?: string | null;
  lastName: string;
}) => [person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ');

const linkDisplayName = (link: FamilyLink) =>
  link.isExternal
    ? (link.relatedExternalName ?? '')
    : link.related
      ? displayName(link.related)
      : '';

const DetailRow = ({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string;
  /** Span both columns in a dual-column detail grid. */
  wide?: boolean;
}) => (
  <div
    className={
      wide
        ? 'grid grid-cols-1 gap-0.5 text-body-sm sm:col-span-2 sm:grid-cols-[10rem_1fr] sm:gap-2'
        : 'grid grid-cols-1 gap-0.5 text-body-sm sm:grid-cols-[10rem_1fr] sm:gap-2'
    }
  >
    <dt className="text-fg-secondary">{label}</dt>
    <dd className="min-w-0 break-words">{value}</dd>
  </div>
);

export const MemberDetailPage = () => {
  const { memberId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const listState = useMemo(() => parseMembersListState(searchParams), [searchParams]);
  const membersListLink = membersListPath(listState);
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);
  const church = useChurchStore((state) => state.church);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const [tab, setTab] = useState('details');
  const [editOpen, setEditOpen] = useState(false);
  const [recordGivingOpen, setRecordGivingOpen] = useState(false);
  const [recordGivingIntent, setRecordGivingIntent] = useState<'any' | 'tithe' | 'offering'>(
    'any',
  );
  const [form, setForm] = useState<MemberFormState>(emptyMemberForm());
  const [familyOpen, setFamilyOpen] = useState(false);
  const [relativeIsMember, setRelativeIsMember] = useState(true);
  const [relatedMemberId, setRelatedMemberId] = useState<string | null>(null);
  const [relatedExternalName, setRelatedExternalName] = useState('');
  const [relatedExternalPhone, setRelatedExternalPhone] = useState<string | null>(null);
  const [relationship, setRelationship] = useState<FamilyRelationship | null>('parent');
  const [familyNotes, setFamilyNotes] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [deceasedOpen, setDeceasedOpen] = useState(false);
  const [dateOfDeath, setDateOfDeath] = useState<string | null>(null);
  const [burialDate, setBurialDate] = useState<string | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferDestinationKind, setTransferDestinationKind] = useState<'congregation' | 'branch'>(
    'congregation',
  );
  const [transferDestination, setTransferDestination] = useState('');
  const [transferLocation, setTransferLocation] = useState('');
  const [transferBranchId, setTransferBranchId] = useState<string | null>(null);
  const [transferDate, setTransferDate] = useState<string | null>(null);
  const [statusConfirm, setStatusConfirm] = useState<'promote' | 'inactivate' | 'restore' | null>(
    null,
  );
  const [familyRemoveId, setFamilyRemoveId] = useState<string | null>(null);

  const dash = t('members.detail.emptyValue');

  const member = useQuery({
    queryKey: ['people', 'member', memberId],
    queryFn: () => api.get<MemberRecord>(`/people/members/${memberId}`),
    enabled: Boolean(memberId),
  });

  const adjacent = useQuery({
    queryKey: queryKeys.list(church?.id ?? null, 'people/members/adjacent', {
      memberId,
      ...listState,
    }),
    queryFn: () => {
      const params = new URLSearchParams(membersListToApiQuery({ ...listState, page: 1 }));
      return api.get<{
        prevId: string | null;
        nextId: string | null;
        position: number;
        total: number;
      }>(`/people/members/${memberId}/adjacent?${params.toString()}`);
    },
    enabled: Boolean(church?.id && memberId),
  });

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName.toLowerCase();
      if (
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable
      ) {
        return;
      }
      if (!adjacent.data) return;
      if (event.key === 'k' || event.key === 'ArrowLeft') {
        if (adjacent.data.prevId) {
          event.preventDefault();
          void navigate(memberDetailPath(adjacent.data.prevId, listState));
        }
      }
      if (event.key === 'j' || event.key === 'ArrowRight') {
        if (adjacent.data.nextId) {
          event.preventDefault();
          void navigate(memberDetailPath(adjacent.data.nextId, listState));
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [adjacent.data, listState, navigate]);

  const history = useQuery({
    queryKey: ['people', 'member', memberId, 'changes'],
    queryFn: () => api.get<{ data: ChangeRow[] }>(`/people/members/${memberId}/changes`),
    enabled: Boolean(memberId) && tab === 'history',
  });

  const familyTree = useQuery({
    queryKey: ['people', 'member', memberId, 'family-tree'],
    queryFn: () => api.get<FamilyTree>(`/people/members/${memberId}/family/tree`),
    enabled: Boolean(memberId) && tab === 'family',
  });

  const peopleSearch = useQuery({
    queryKey: ['people', 'members', 'picker', memberQuery],
    queryFn: () =>
      api.get<{ data: MemberRecord[] }>(
        `/people/members?${new URLSearchParams({
          page: '1',
          pageSize: '20',
          ...(memberQuery ? { q: memberQuery } : {}),
        }).toString()}`,
      ),
    enabled: familyOpen,
  });

  const familyConnection = useQuery({
    queryKey: ['people', 'member', memberId, 'family-connection', relatedMemberId],
    queryFn: () =>
      api.get<{
        connected: boolean;
        direct: boolean;
        relationship: FamilyRelationship | null;
        degree: number | null;
        inferred: boolean;
      }>(`/people/members/${memberId}/family/connection/${relatedMemberId}`),
    enabled: familyOpen && Boolean(memberId && relatedMemberId),
  });

  const branchOptionsQuery = useQuery({
    queryKey: ['people', 'transfer-branches', church?.id],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; campusName: string; isHead: boolean }> }>(
        '/people/members/transfer-branches',
      ),
    enabled: Boolean(
      transferOpen &&
        church &&
        isModuleEnabled('branches') &&
        allowsFeature('multi_branch'),
    ),
  });

  useEffect(() => {
    if (!member.data || !editOpen) return;
    setForm(memberToForm(member.data));
  }, [member.data, editOpen]);

  const memberOptions = useMemo(
    () =>
      (peopleSearch.data?.data ?? [])
        .filter((row) => row.id !== memberId)
        .map((row) => ({
          id: row.id,
          label: displayName(row),
          description: row.phone ?? undefined,
        })),
    [peopleSearch.data?.data, memberId],
  );

  const selectedRelated = useMemo(() => {
    const row = peopleSearch.data?.data.find((item) => item.id === relatedMemberId);
    return row
      ? {
          id: row.id,
          label: displayName(row),
          description: row.phone ?? undefined,
          gender: row.gender,
        }
      : null;
  }, [peopleSearch.data?.data, relatedMemberId]);

  const transferBranchChoices = useMemo(
    () => branchOptionsQuery.data?.data ?? [],
    [branchOptionsQuery.data?.data],
  );
  const showBranchTransferOption =
    Boolean(church && isModuleEnabled('branches') && allowsFeature('multi_branch')) &&
    transferBranchChoices.length > 0;
  const transferBranchListboxOptions = useMemo(
    () =>
      transferBranchChoices.map((campus) => ({
        value: campus.id,
        label: campus.isHead
          ? `${campus.campusName} (${t('members.transfer.headOffice')})`
          : campus.campusName,
      })),
    [transferBranchChoices, t],
  );
  const canSubmitTransfer =
    transferDestinationKind === 'branch'
      ? Boolean(transferBranchId)
      : Boolean(transferDestination.trim());

  const invalidateMember = () => {
    void queryClient.invalidateQueries({ queryKey: ['people', 'member', memberId] });
    void queryClient.invalidateQueries({ queryKey: ['people', 'members'] });
  };

  const action = useMutation({
    mutationFn: (path: string) => api.post<MemberRecord>(path, {}),
    onSuccess: () => {
      toast.success(t('members.form.saved'));
      setStatusConfirm(null);
      invalidateMember();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const save = useMutation({
    mutationFn: () => api.patch<MemberRecord>(`/people/members/${memberId}`, formToPayload(form)),
    onSuccess: () => {
      toast.success(t('members.form.saved'));
      setEditOpen(false);
      invalidateMember();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const markDeceased = useMutation({
    mutationFn: () =>
      api.post<MemberRecord>(`/people/members/${memberId}/deceased`, {
        dateOfDeath,
        burialDate,
      }),
    onSuccess: () => {
      toast.success(t('members.deceased.saved'));
      setDeceasedOpen(false);
      invalidateMember();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const sendClaimInvite = useMutation({
    mutationFn: () =>
      api.post<{ inviteId: string; expiresAt: string }>(
        `/people/members/${memberId}/profile-claim/send`,
        {},
      ),
    onSuccess: () => {
      toast.success(t('members.detail.claimInviteSent'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const transfer = useMutation({
    mutationFn: () =>
      api.post<MemberRecord>(`/people/members/${memberId}/transfer`, {
        transferDestinationBranchId:
          transferDestinationKind === 'branch' ? transferBranchId : null,
        transferDestinationChurch:
          transferDestinationKind === 'congregation' ? transferDestination.trim() || null : null,
        transferDestinationLocation:
          transferDestinationKind === 'congregation' ? transferLocation.trim() || null : null,
        transferDate,
      }),
    onSuccess: () => {
      toast.success(t('members.transfer.saved'));
      setTransferOpen(false);
      setTransferDestinationKind('congregation');
      setTransferDestination('');
      setTransferLocation('');
      setTransferBranchId(null);
      setTransferDate(null);
      invalidateMember();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const addFamily = useMutation({
    mutationFn: () =>
      api.post(`/people/members/${memberId}/family`, {
        ...(relativeIsMember
          ? { relatedMemberId }
          : {
              relatedExternalName: relatedExternalName.trim(),
              relatedExternalPhone: relatedExternalPhone || null,
            }),
        relationship,
        notes: familyNotes.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('members.family.saved'));
      setFamilyOpen(false);
      setRelativeIsMember(true);
      setRelatedMemberId(null);
      setRelatedExternalName('');
      setRelatedExternalPhone(null);
      setFamilyNotes('');
      void queryClient.invalidateQueries({ queryKey: ['people', 'member', memberId, 'family-tree'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeFamily = useMutation({
    mutationFn: (relationshipId: string) =>
      api.delete(`/people/members/${memberId}/family/${relationshipId}`),
    onSuccess: () => {
      toast.success(t('members.family.removed'));
      setFamilyRemoveId(null);
      void queryClient.invalidateQueries({ queryKey: ['people', 'member', memberId, 'family-tree'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  if (member.isLoading) {
    return (
      <div>
        <PageHeader
          title={t('members.title')}
          breadcrumbs={[
            { label: t('members.title'), to: membersListLink },
            { label: tCommon('state.loading') },
          ]}
        />
        <div className="flex min-h-[24rem] items-center justify-center" role="status">
          <Spinner className="size-8 text-brand" />
        </div>
      </div>
    );
  }

  if (member.isError || !member.data) {
    return (
      <div>
        <PageHeader
          title={tCommon('state.notFoundTitle')}
          breadcrumbs={[
            { label: t('members.title'), to: membersListLink },
            { label: tCommon('state.notFoundTitle') },
          ]}
        />
        <ErrorState
          title={tCommon('state.notFoundTitle')}
          body={tCommon('state.notFoundBody')}
          onRetry={() => void member.refetch()}
          retryLabel={tCommon('action.retry')}
          full
        />
      </div>
    );
  }

  const row = member.data;
  const fullName = displayName(row);
  const canViewGiving =
    isModuleEnabled('giving') &&
    hasPermission(membership, PERMISSIONS.GIVING_VIEW_MEMBER_HISTORY);
  const canRecordGiving =
    isModuleEnabled('giving') && hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const canEditMember = hasPermission(membership, PERMISSIONS.PEOPLE_EDIT);
  const canManageStatus = hasPermission(membership, PERMISSIONS.PEOPLE_MANAGE_STATUS);

  const show = (value: string | null | undefined) => value || dash;
  const showDate = (value: string | null | undefined) => (value ? formatDate(value) : dash);
  const showBool = (value: boolean | null | undefined) => {
    if (value === null || value === undefined) return dash;
    return value ? tCommon('boolean.yes') : tCommon('boolean.no');
  };
  const showEnum = (namespace: string, value: string | null | undefined) =>
    value ? t(`${namespace}.${value}` as never) : dash;

  const sourceGatheringLabel = (member: MemberRecord) => {
    if (!member.sourceProgramName && !member.sourceOccurrenceDate) return dash;
    if (member.sourceProgramName && member.sourceOccurrenceDate) {
      return `${member.sourceProgramName} · ${formatDate(member.sourceOccurrenceDate)}`;
    }
    return member.sourceProgramName ?? (member.sourceOccurrenceDate ? formatDate(member.sourceOccurrenceDate) : dash);
  };

  const treeGroups: Array<{ key: Exclude<keyof FamilyTree, 'peerLinks'>; title: string }> = [
    { key: 'parents', title: t('members.family.tree.parents') },
    { key: 'unclesAunts', title: t('members.family.tree.unclesAunts') },
    { key: 'spouses', title: t('members.family.tree.spouses') },
    { key: 'siblings', title: t('members.family.tree.siblings') },
    { key: 'children', title: t('members.family.tree.children') },
    { key: 'niecesNephews', title: t('members.family.tree.niecesNephews') },
    { key: 'cousins', title: t('members.family.tree.cousins') },
    { key: 'grandparents', title: t('members.family.tree.grandparents') },
    { key: 'grandchildren', title: t('members.family.tree.grandchildren') },
    { key: 'other', title: t('members.family.tree.other') },
  ];

  const familyRows = treeGroups.flatMap((group) => familyTree.data?.[group.key] ?? []);

  let familyRemoveName: string | null = null;
  if (familyRemoveId) {
    const match = familyRows.find((link) => link.id === familyRemoveId);
    if (match) familyRemoveName = linkDisplayName(match);
  }

  const familyColumns: DataTableColumn<FamilyLink>[] = [
    {
      id: 'name',
      header: t('members.family.columns.name'),
      cell: (link) => {
        const name = linkDisplayName(link);
        return (
          <MemberNameLink
            memberId={link.isExternal ? null : link.related?.id}
            name={name}
            photoUrl={link.related?.photoUrl ?? null}
            showPhoto
            className="font-medium"
          />
        );
      },
    },
    {
      id: 'relationship',
      header: t('members.family.columns.relationship'),
      cell: (link) =>
        formatFamilyRelationship(t, link.relationship, link.related?.gender ?? null, 'my'),
    },
    {
      id: 'gender',
      header: t('members.family.columns.gender'),
      cell: (link) =>
        link.related?.gender ? t(`members.gender.${link.related.gender}`) : dash,
    },
    {
      id: 'notes',
      header: t('members.family.columns.notes'),
      cell: (link) => link.notes || dash,
      hideBelowMd: true,
    },
    {
      id: 'actions',
      header: tCommon('table.actions'),
      align: 'right',
      cell: (link) => (
        <Button
          size="sm"
          variant="outline"
          onClick={(event) => {
            event.stopPropagation();
            setFamilyRemoveId(link.id);
          }}
        >
          {t('members.family.remove')}
        </Button>
      ),
    },
  ];

  const historyColumns: DataTableColumn<ChangeRow>[] = [
    {
      id: 'when',
      header: t('members.detail.historyColumns.when'),
      cell: (change) => formatDateTime(change.createdAt),
    },
    {
      id: 'who',
      header: t('members.detail.historyColumns.who'),
      cell: (change) => change.actorName ?? dash,
    },
    {
      id: 'field',
      header: t('members.detail.historyColumns.field'),
      cell: (change) => memberHistoryFieldLabel(change.field, t),
    },
    {
      id: 'before',
      header: t('members.detail.historyColumns.before'),
      cell: (change) =>
        formatMemberHistoryValue(
          change.field,
          change.beforeValue,
          change.afterValue,
          'before',
          t,
          tCommon,
        ) ?? dash,
    },
    {
      id: 'after',
      header: t('members.detail.historyColumns.after'),
      cell: (change) =>
        formatMemberHistoryValue(
          change.field,
          change.afterValue,
          change.beforeValue,
          'after',
          t,
          tCommon,
        ) ?? dash,
    },
  ];

  return (
    <div>
      <PageHeader
        title={fullName}
        breadcrumbs={[
          { label: t('members.title'), to: membersListLink },
          { label: fullName },
        ]}
        meta={
          <>
            <Badge variant={row.status === 'active' ? 'success' : 'neutral'}>
              {t(`members.status.${row.status}`)}
            </Badge>
            <span className="text-body-sm text-fg-secondary">
              {t(`members.type.${row.memberType}`)}
            </span>
          </>
        }
        headerActions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canEditMember ? (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                {tCommon('action.edit')}
              </Button>
            ) : null}
            {canRecordGiving ? (
              <Button
                size="sm"
                variant={
                  (row.memberType === 'visitor' || row.memberType === 'first_timer') &&
                  row.status === 'active'
                    ? 'outline'
                    : 'primary'
                }
                onClick={() => {
                  setRecordGivingIntent('any');
                  setRecordGivingOpen(true);
                }}
              >
                {t('members.detail.giving.record')}
              </Button>
            ) : null}
            {canEditMember && !row.userId && row.status === 'active' ? (
              <Button
                size="sm"
                variant="outline"
                disabled={sendClaimInvite.isPending}
                onClick={() => sendClaimInvite.mutate()}
              >
                {t('members.detail.sendClaimInvite')}
              </Button>
            ) : null}
            {canManageStatus &&
            (row.memberType === 'visitor' || row.memberType === 'first_timer') &&
            row.status === 'active' ? (
              <Button size="sm" onClick={() => setStatusConfirm('promote')}>
                {t('members.actions.promote')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="mb-4">
        <MemberDetailNavigation memberId={memberId} listState={listState} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="details">{t('members.detail.tabs.details')}</TabsTrigger>
          <TabsTrigger value="family">{t('members.detail.tabs.family')}</TabsTrigger>
          {canViewGiving ? (
            <TabsTrigger value="giving">{t('members.detail.tabs.giving')}</TabsTrigger>
          ) : null}
          <TabsTrigger value="history">{t('members.detail.tabs.history')}</TabsTrigger>
          {canManageStatus ? (
            <TabsTrigger value="status">{t('members.detail.tabs.status')}</TabsTrigger>
          ) : null}
          <TabsTrigger value="files">{t('members.detail.tabs.files')}</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('members.detail.sections.identity')}</CardTitle>
            </CardHeader>
            <CardContent>
              {row.secondaryHidden ? (
                <p className="mb-3 text-body-sm text-fg-secondary">{t('members.secondaryHidden')}</p>
              ) : null}
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                <dl className="grid min-w-0 flex-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                  <DetailRow
                    label={t('members.form.membershipNumber')}
                    value={show(row.membershipNumber)}
                  />
                  <DetailRow label={t('members.form.title')} value={show(row.title)} />
                  <DetailRow label={t('members.form.firstName')} value={row.firstName} />
                  <DetailRow label={t('members.form.middleName')} value={show(row.middleName)} />
                  <DetailRow label={t('members.form.lastName')} value={row.lastName} />
                  {row.secondaryHidden ? null : (
                    <DetailRow label={t('members.form.maidenName')} value={show(row.maidenName)} />
                  )}
                  <DetailRow
                    label={t('members.form.gender')}
                    value={showEnum('members.gender', row.gender)}
                  />
                  {row.secondaryHidden ? null : (
                    <>
                      <DetailRow
                        label={t('members.form.dateOfBirth')}
                        value={showDate(row.dateOfBirth)}
                      />
                      <DetailRow
                        label={t('members.form.placeOfBirth')}
                        value={show(row.placeOfBirth)}
                      />
                      <DetailRow
                        label={t('members.form.maritalStatus')}
                        value={showEnum('members.marital', row.maritalStatus)}
                      />
                      <DetailRow
                        label={t('members.form.isCommunicant')}
                        value={showBool(row.isCommunicant)}
                      />
                      <DetailRow label={t('members.form.motherName')} value={show(row.motherName)} />
                      <DetailRow label={t('members.form.fatherName')} value={show(row.fatherName)} />
                      <DetailRow label={t('members.form.occupation')} value={show(row.occupation)} />
                      <DetailRow label={t('members.form.employer')} value={show(row.employer)} />
                      <DetailRow
                        label={t('members.form.educationBackground')}
                        value={show(row.educationBackground)}
                        wide
                      />
                      <DetailRow
                        label={t('members.form.employmentBackground')}
                        value={show(row.employmentBackground)}
                        wide
                      />
                    </>
                  )}
                  <DetailRow label={t('members.form.nationalId')} value={show(row.nationalId)} />
                </dl>
                <MemberPhoto
                  src={row.photoUrl}
                  name={fullName}
                  size="2xl"
                  className="shrink-0 self-center lg:self-start"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('members.detail.sections.contact')}</CardTitle>
            </CardHeader>
            <CardContent>
              {row.contactHidden ? (
                <p className="mb-3 text-body-sm text-fg-secondary">{t('members.contactHidden')}</p>
              ) : null}
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                <DetailRow label={t('members.form.phone')} value={show(row.phone)} />
                <DetailRow label={t('members.form.alternatePhone')} value={show(row.alternatePhone)} />
                <DetailRow label={t('members.form.email')} value={show(row.email)} />
                <DetailRow
                  label={t('members.form.appConnect.email')}
                  value={show(row.appConnectEmail)}
                />
                <DetailRow
                  label={t('members.form.appConnect.phone')}
                  value={show(row.appConnectPhone)}
                />
                <DetailRow
                  label={t('members.detail.appLinked')}
                  value={row.userId ? tCommon('boolean.yes') : t('members.detail.appNotLinked')}
                />
                <DetailRow label={t('members.form.country')} value={show(row.country)} />
                <DetailRow
                  label={t('members.form.residentialAddress')}
                  value={show(row.address)}
                  wide
                />
                <DetailRow label={t('members.form.city')} value={show(row.city)} />
                <DetailRow label={t('members.form.region')} value={show(row.region)} />
                <DetailRow
                  label={t('members.form.postalAddress')}
                  value={show(row.postalAddress)}
                  wide
                />
                <DetailRow label={t('members.form.postalCity')} value={show(row.postalCity)} />
                <DetailRow label={t('members.form.postalRegion')} value={show(row.postalRegion)} />
                <DetailRow label={t('members.form.postalCountry')} value={show(row.postalCountry)} />
                <DetailRow label={t('members.form.hometown')} value={show(row.hometown)} />
                <DetailRow
                  label={t('members.form.hometownRegion')}
                  value={show(row.hometownRegion)}
                />
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('members.detail.sections.membership')}</CardTitle>
            </CardHeader>
            <CardContent>
              {row.secondaryHidden ? (
                <p className="mb-3 text-body-sm text-fg-secondary">{t('members.secondaryHidden')}</p>
              ) : null}
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                <DetailRow
                  label={t('members.form.memberType')}
                  value={t(`members.type.${row.memberType}`)}
                />
                <DetailRow label={t('members.form.joinedAt')} value={showDate(row.joinedAt)} />
                {row.secondaryHidden ? null : (
                  <>
                    <DetailRow
                      label={t('members.form.receptionMethod')}
                      value={showEnum('members.reception', row.receptionMethod)}
                    />
                    <DetailRow label={t('members.form.formerChurch')} value={show(row.formerChurch)} />
                  </>
                )}
                <DetailRow
                  label={t('members.form.sourceKind')}
                  value={showEnum('members.sourceKind', row.sourceKind)}
                />
                {row.sourceKind === 'gathering' ? (
                  <DetailRow
                    label={t('members.form.sourceGathering')}
                    value={sourceGatheringLabel(row)}
                  />
                ) : null}
                {row.sourceKind === 'invited' ? (
                  <DetailRow
                    label={t('members.form.invitedByName')}
                    value={show(row.invitedByName)}
                  />
                ) : null}
                {row.sourceDetail && row.sourceKind !== 'invited' ? (
                  <DetailRow label={t('members.form.sourceDetail')} value={show(row.sourceDetail)} />
                ) : null}
                <DetailRow
                  label={t('members.form.firstContactOn')}
                  value={showDate(row.firstContactOn)}
                />
                {row.status === 'transferred' ? (
                  <>
                    <DetailRow
                      label={t('members.transfer.destination')}
                      value={show(row.transferDestinationChurch)}
                    />
                    {row.transferDestinationBranchName ? (
                      <DetailRow
                        label={t('members.transfer.branch')}
                        value={show(row.transferDestinationBranchName)}
                      />
                    ) : (
                      <DetailRow
                        label={t('members.transfer.location')}
                        value={show(row.transferDestinationLocation)}
                      />
                    )}
                    <DetailRow
                      label={t('members.transfer.date')}
                      value={showDate(row.transferDate)}
                    />
                  </>
                ) : null}
                {row.secondaryHidden ? null : (
                  <>
                    <DetailRow label={t('members.form.baptismDate')} value={showDate(row.baptismDate)} />
                    <DetailRow label={t('members.form.baptismNumber')} value={show(row.baptismNumber)} />
                    <DetailRow
                      label={t('members.form.baptismType')}
                      value={showEnum('members.baptismType', row.baptismType)}
                    />
                    <DetailRow label={t('members.form.baptismPlace')} value={show(row.baptismPlace)} />
                    <DetailRow
                      label={t('members.form.baptismMinister')}
                      value={show(row.baptismMinister)}
                    />
                    <DetailRow
                      label={t('members.form.confirmationDate')}
                      value={showDate(row.confirmationDate)}
                    />
                    <DetailRow
                      label={t('members.form.confirmationNumber')}
                      value={show(row.confirmationNumber)}
                    />
                    <DetailRow
                      label={t('members.form.confirmationPlace')}
                      value={show(row.confirmationPlace)}
                    />
                  </>
                )}
                {row.status === 'deceased' && !row.secondaryHidden ? (
                  <>
                    <DetailRow
                      label={t('members.deceased.dateOfDeath')}
                      value={showDate(row.dateOfDeath)}
                    />
                    <DetailRow
                      label={t('members.deceased.burialDate')}
                      value={showDate(row.burialDate)}
                    />
                  </>
                ) : null}
              </dl>
            </CardContent>
          </Card>

          <MemberInvolvementCard memberId={row.id} />

          <Card>
            <CardHeader>
              <CardTitle>{t('members.detail.sections.marriage')}</CardTitle>
            </CardHeader>
            <CardContent>
              {row.secondaryHidden ? (
                <p className="text-body-sm text-fg-secondary">{t('members.secondaryHidden')}</p>
              ) : (
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                <DetailRow
                  label={t('members.form.marriageDate')}
                  value={showDate(row.marriageDate)}
                />
                <DetailRow
                  label={t('members.form.marriageType')}
                  value={showEnum('members.marriageType', row.marriageType)}
                />
                <DetailRow
                  label={t('members.form.marriagePlace')}
                  value={show(row.marriagePlace)}
                  wide
                />
                <DetailRow
                  label={t('members.form.marriageMinister')}
                  value={show(row.marriageMinister)}
                />
                {row.maritalStatus === 'married' ? (
                  <DetailRow label={t('members.form.spouseMember')} value={show(row.spouseName)} />
                ) : null}
              </dl>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('members.detail.sections.emergency')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {row.emergencyContactMemberId ? (
                  <div className="grid grid-cols-[8rem_1fr] gap-2 text-body-sm sm:grid-cols-[10rem_1fr]">
                    <dt className="text-fg-secondary">{t('members.form.emergencyContactMember')}</dt>
                    <dd className="min-w-0 break-words">
                      <MemberNameLink
                        memberId={row.emergencyContactMemberId}
                        name={show(row.emergencyContactName)}
                        className="font-medium"
                      />
                    </dd>
                  </div>
                ) : (
                  <DetailRow
                    label={t('members.form.emergencyContactName')}
                    value={show(row.emergencyContactName)}
                  />
                )}
                <DetailRow
                  label={t('members.form.emergencyContactPhone')}
                  value={show(row.emergencyContactPhone)}
                />
                <DetailRow
                  label={t('members.form.emergencyContactRelationship')}
                  value={show(row.emergencyContactRelationship)}
                  wide
                />
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('members.detail.sections.care')}</CardTitle>
            </CardHeader>
            <CardContent>
              {row.secondaryHidden ? (
                <p className="mb-3 text-body-sm text-fg-secondary">{t('members.secondaryHidden')}</p>
              ) : null}
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                <DetailRow
                  label={t('members.form.isDistantMember')}
                  value={row.isDistantMember ? tCommon('boolean.yes') : tCommon('boolean.no')}
                />
                {row.secondaryHidden ? null : (
                  <>
                    <DetailRow
                      label={t('members.form.hasDisability')}
                      value={row.hasDisability ? tCommon('boolean.yes') : tCommon('boolean.no')}
                    />
                    <DetailRow
                      label={t('members.form.disabilityNotes')}
                      value={show(row.disabilityNotes)}
                      wide
                    />
                    <DetailRow label={t('members.form.notes')} value={show(row.notes)} wide />
                  </>
                )}
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="family" className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-card-title font-semibold">{t('members.family.title')}</h2>
              <p className="text-body-sm text-fg-secondary">{t('members.family.description')}</p>
            </div>
            <Button size="sm" onClick={() => setFamilyOpen(true)}>
              {t('members.family.add')}
            </Button>
          </div>

          <DataTable
            rows={familyRows}
            columns={familyColumns}
            rowKey={(link) => link.id}
            loading={familyTree.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">{t('members.family.empty')}</p>
            }
          />
        </TabsContent>

        {canViewGiving ? (
          <TabsContent value="giving">
            <MemberGivingTab
              memberId={row.id}
              canRecord={canRecordGiving}
              onRecord={(intent) => {
                setRecordGivingIntent(intent ?? 'any');
                setRecordGivingOpen(true);
              }}
            />
          </TabsContent>
        ) : null}

        <TabsContent value="history">
          <DataTable
            rows={history.data?.data ?? []}
            columns={historyColumns}
            rowKey={(change) => change.id}
            loading={history.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">{t('members.detail.historyEmpty')}</p>
            }
          />
        </TabsContent>

        <TabsContent value="status">
          <Card>
            <CardHeader>
              <CardTitle>{t('members.detail.status.title')}</CardTitle>
              <CardDescription>{t('members.detail.status.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-border px-3 py-3">
                <p className="text-body-sm font-medium">{t('members.detail.status.current')}</p>
                <Badge
                  className="mt-1"
                  variant={row.status === 'active' ? 'success' : 'neutral'}
                >
                  {t(`members.status.${row.status}`)}
                </Badge>
                {row.status === 'transferred' ? (
                  <dl className="mt-3 grid gap-x-8 gap-y-3 sm:grid-cols-3">
                    <div className="flex flex-col gap-0.5 text-body-sm">
                      <dt className="text-fg-secondary">{t('members.transfer.destination')}</dt>
                      <dd className="min-w-0 break-words">
                        {show(row.transferDestinationChurch)}
                      </dd>
                    </div>
                    <div className="flex flex-col gap-0.5 text-body-sm">
                      <dt className="text-fg-secondary">
                        {row.transferDestinationBranchName
                          ? t('members.transfer.branch')
                          : t('members.transfer.location')}
                      </dt>
                      <dd className="min-w-0 break-words">
                        {show(
                          row.transferDestinationBranchName ?? row.transferDestinationLocation,
                        )}
                      </dd>
                    </div>
                    <div className="flex flex-col gap-0.5 text-body-sm">
                      <dt className="text-fg-secondary">{t('members.transfer.date')}</dt>
                      <dd className="min-w-0 break-words">{showDate(row.transferDate)}</dd>
                    </div>
                  </dl>
                ) : null}
              </div>

              {row.status === 'active' ? (
                <div className="flex flex-wrap items-start justify-between gap-3 border-t border-border pt-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm font-medium">{t('members.actions.inactivate')}</p>
                    <Tip tipId="members.status.inactivate" />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setStatusConfirm('inactivate')}>
                    {t('members.actions.inactivate')}
                  </Button>
                </div>
              ) : null}

              {row.status === 'active' || row.status === 'inactive' ? (
                <div className="flex flex-wrap items-start justify-between gap-3 border-t border-border pt-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm font-medium">{t('members.actions.transfer')}</p>
                    <Tip tipId="members.status.transfer" />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setTransferDestinationKind('congregation');
                      setTransferDestination('');
                      setTransferLocation('');
                      setTransferBranchId(null);
                      setTransferDate(null);
                      setTransferOpen(true);
                    }}
                  >
                    {t('members.actions.transfer')}
                  </Button>
                </div>
              ) : null}

              {row.status !== 'deceased' ? (
                <div className="flex flex-wrap items-start justify-between gap-3 border-t border-border pt-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm font-medium">{t('members.actions.deceased')}</p>
                    <Tip tipId="members.status.deceased" />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setDeceasedOpen(true)}>
                    {t('members.actions.deceased')}
                  </Button>
                </div>
              ) : null}

              {row.status === 'inactive' || row.status === 'transferred' ? (
                <div className="flex flex-wrap items-start justify-between gap-3 border-t border-border pt-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm font-medium">{t('members.actions.restore')}</p>
                    <Tip tipId="members.status.restore" />
                  </div>
                  <Button size="sm" onClick={() => setStatusConfirm('restore')}>
                    {t('members.actions.restore')}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="files">
          <MemberFilesCard
            key={memberId}
            memberId={memberId}
            memberName={fullName}
            canEdit={canEditMember}
            status={row.status}
          />
        </TabsContent>
      </Tabs>

      <FormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title={t('members.form.editTitle')}
        size="2xl"
        basicLabel={t('members.form.basicTab')}
        moreLabel={t('members.form.moreTab')}
        basic={<BasicMemberFields value={form} onChange={setForm} />}
        more={<MoreMemberFields value={form} onChange={setForm} />}
        submitLabel={t('members.form.save')}
        cancelLabel={tCommon('action.cancel')}
        closeLabel={tCommon('action.close')}
        submitting={save.isPending}
        onSubmit={async () => {
          await save.mutateAsync();
        }}
      />

      {canRecordGiving ? (
        <MemberRecordGivingDialog
          memberId={row.id}
          open={recordGivingOpen}
          onOpenChange={setRecordGivingOpen}
          intent={recordGivingIntent}
        />
      ) : null}

      <Dialog open={familyOpen} onOpenChange={setFamilyOpen}>
        <DialogContent size="lg" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('members.family.addTitle')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex min-h-0 flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!relationship) return;
              if (relativeIsMember && !relatedMemberId) return;
              if (!relativeIsMember && !relatedExternalName.trim()) return;
              addFamily.mutate();
            }}
          >
            <DialogBody>
              <div className="flex flex-col gap-3">
                <Tip tipId="members.household.relationship" />
                <SwitchField
                  id="relative-is-member"
                  label={t('members.family.relativeIsMember')}
                  checked={relativeIsMember}
                  onCheckedChange={(checked) => {
                    setRelativeIsMember(checked);
                    setRelatedMemberId(null);
                    setRelatedExternalName('');
                    setRelatedExternalPhone(null);
                  }}
                />
                {relativeIsMember ? (
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('members.family.person')}</Label>
                    <EntityCombobox
                      value={relatedMemberId}
                      onChange={setRelatedMemberId}
                      options={memberOptions}
                      selected={selectedRelated}
                      onQueryChange={setMemberQuery}
                      loading={peopleSearch.isFetching}
                      placeholder={t('members.family.personPlaceholder')}
                      emptyMessage={t('members.family.personEmpty')}
                      clearLabel={t('members.form.clear')}
                      popoverPortal="dialog"
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <Label>{t('members.family.externalName')}</Label>
                      <Input
                        value={relatedExternalName}
                        onChange={(event) => setRelatedExternalName(event.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label>{t('members.family.externalPhone')}</Label>
                      <PhoneInput
                        value={relatedExternalPhone}
                        onChange={setRelatedExternalPhone}
                        popoverPortal="dialog"
                      />
                    </div>
                  </>
                )}
                {relativeIsMember && relatedMemberId && familyConnection.data ? (
                  <p className="text-body-sm text-fg-secondary">
                    {familyConnection.data.connected && familyConnection.data.relationship
                      ? familyConnection.data.direct
                        ? t('members.family.connection.direct', {
                            name: selectedRelated?.label ?? '',
                            relationship: formatFamilyRelationship(
                              t,
                              familyConnection.data.relationship,
                              selectedRelated?.gender ?? null,
                              'my',
                            ),
                          })
                        : t('members.family.connection.inferred', {
                            name: selectedRelated?.label ?? '',
                            relationship: formatFamilyRelationship(
                              t,
                              familyConnection.data.relationship,
                              selectedRelated?.gender ?? null,
                            ),
                            count: familyConnection.data.degree ?? 0,
                          })
                      : t('members.family.connection.none')}
                  </p>
                ) : null}
                <div className="flex flex-col gap-1.5">
                  <Label>{t('members.family.relationship')}</Label>
                  <Listbox
                    value={relationship}
                    onChange={(next) => setRelationship(next as FamilyRelationship | null)}
                    options={FAMILY_RELATIONSHIPS.map((value) => ({
                      value,
                      label: t(`members.relationship.my.${value}`),
                    }))}
                    popoverPortal="dialog"
                  />
                  {relatedMemberId && relationship && row ? (
                    <p className="text-body-sm text-fg-secondary">
                      {formatFamilyRelationshipPreview(
                        t,
                        relationship,
                        selectedRelated?.gender ?? null,
                        {
                          name: selectedRelated?.label ?? '',
                          member: displayName(row),
                        },
                      )}
                    </p>
                  ) : (
                    <p className="text-body-sm text-fg-secondary">
                      {t('members.family.relationshipHint', { name: displayName(row) })}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>{t('members.family.notes')}</Label>
                  <Textarea
                    value={familyNotes}
                    onChange={(event) => setFamilyNotes(event.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFamilyOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={addFamily.isPending}
                disabled={
                  !relationship ||
                  (relativeIsMember ? !relatedMemberId : !relatedExternalName.trim())
                }
              >
                {t('members.family.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deceasedOpen} onOpenChange={setDeceasedOpen}>
        <DialogContent size="md" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('members.deceased.title')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex min-h-0 flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              markDeceased.mutate();
            }}
          >
            <DialogBody>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label>{t('members.deceased.dateOfDeath')}</Label>
                  <DatePicker
                    value={dateOfDeath}
                    onChange={setDateOfDeath}
                    popoverPortal="dialog"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>{t('members.deceased.burialDate')}</Label>
                  <DatePicker value={burialDate} onChange={setBurialDate} popoverPortal="dialog" />
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeceasedOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={markDeceased.isPending}>
                {t('members.deceased.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent size="md" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('members.transfer.title')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex min-h-0 flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              transfer.mutate();
            }}
          >
            <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
              {showBranchTransferOption ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('members.transfer.destinationKind')}</Label>
                  <Listbox
                    value={transferDestinationKind}
                    onChange={(next) => {
                      const kind = next as 'congregation' | 'branch';
                      setTransferDestinationKind(kind);
                      if (kind === 'branch') {
                        setTransferDestination('');
                        setTransferLocation('');
                      } else {
                        setTransferBranchId(null);
                      }
                    }}
                    options={[
                      {
                        value: 'congregation',
                        label: t('members.transfer.destinationKindCongregation'),
                      },
                      {
                        value: 'branch',
                        label: t('members.transfer.destinationKindBranch'),
                      },
                    ]}
                    popoverPortal="dialog"
                  />
                </div>
              ) : null}

              {transferDestinationKind === 'branch' && showBranchTransferOption ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('members.transfer.branch')}</Label>
                  <Listbox
                    value={transferBranchId}
                    onChange={setTransferBranchId}
                    options={transferBranchListboxOptions}
                    placeholder={t('members.transfer.branchPlaceholder')}
                    popoverPortal="dialog"
                  />
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('members.transfer.destination')}</Label>
                    <Input
                      value={transferDestination}
                      onChange={(event) => setTransferDestination(event.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('members.transfer.location')}</Label>
                    <Input
                      value={transferLocation}
                      onChange={(event) => setTransferLocation(event.target.value)}
                    />
                  </div>
                </>
              )}

              <div className="flex flex-col gap-1.5">
                <Label>{t('members.transfer.date')}</Label>
                <DatePicker
                  value={transferDate}
                  onChange={setTransferDate}
                  popoverPortal="dialog"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransferOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={transfer.isPending} disabled={!canSubmitTransfer}>
                {t('members.transfer.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={statusConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setStatusConfirm(null);
        }}
        title={
          statusConfirm
            ? t(`members.confirm.${statusConfirm}.title`, { name: fullName })
            : ''
        }
        description={
          statusConfirm ? t(`members.confirm.${statusConfirm}.description`, { name: fullName }) : ''
        }
        confirmLabel={statusConfirm ? t(`members.confirm.${statusConfirm}.confirm`) : ''}
        cancelLabel={tCommon('action.cancel')}
        closeLabel={tCommon('action.close')}
        destructive={statusConfirm === 'inactivate'}
        loading={action.isPending}
        onConfirm={() => {
          if (!statusConfirm) return;
          const path =
            statusConfirm === 'promote'
              ? `/people/members/${row.id}/promote`
              : statusConfirm === 'inactivate'
                ? `/people/members/${row.id}/inactivate`
                : `/people/members/${row.id}/restore`;
          action.mutate(path);
        }}
      />

      <ConfirmDialog
        open={familyRemoveId !== null}
        onOpenChange={(open) => {
          if (!open) setFamilyRemoveId(null);
        }}
        title={t('members.confirm.removeFamily.title', {
          name: familyRemoveName ?? '',
        })}
        description={t('members.confirm.removeFamily.description', {
          name: familyRemoveName ?? '',
        })}
        confirmLabel={t('members.confirm.removeFamily.confirm')}
        cancelLabel={tCommon('action.cancel')}
        closeLabel={tCommon('action.close')}
        destructive
        loading={removeFamily.isPending}
        onConfirm={() => {
          if (familyRemoveId) removeFamily.mutate(familyRemoveId);
        }}
      />
    </div>
  );
};
